import React, { useEffect, useState } from "react";
import { Button, CircularProgress } from "@mui/material";
import { cancelListOrder, fetchListRfqItems } from "../../http/api";
import { hasCancellableListItem } from "../../Utils/listRfqStatus";
import { ENHANCED_RFQ_PROTOCOLS } from "../OrderComponents/RfqComponents/RfqConstants";
import { formatListId } from "../../Utils/listOrderId";
import useSnackbar from "../../Utils/useSnackbar";
import RfqFinalConfirmPopup from "../OrderComponents/RfqComponents/RfqFinalConfirmPopup";

// Same tolerant success test the blotter's Cancel List uses (EnhancedRfqActionsCell).
const isSuccess = (response) =>
  response === true ||
  response?.success === true ||
  String(response?.success).toLowerCase() === "true" ||
  response?.data?.success === true ||
  String(response?.data?.success).toLowerCase() === "true" ||
  String(response?.status).toUpperCase() === "SUCCESS" ||
  String(response?.data?.status).toUpperCase() === "SUCCESS";

/**
 * Cancel List, offered on the read-only list view opened from List ID or List Name.
 *
 * Kept visible when cancellation is blocked. Uses the individual-order status
 * gates across the list, plus the ownership and account gates of the clicked row.
 * The bonds are fetched here so the answer is current when the view opens. Sends the same
 * request (/order/list/cancel/{id}) after the same "are you sure" step.
 *
 * @param {object} order the clicked blotter row the view was opened from
 */
const ListCancelButton = ({ order, buttonSx, disabled = false }) => {
  const { showSuccess, showError, SnackbarComponent } = useSnackbar();
  const [items, setItems] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [loadedOrderId, setLoadedOrderId] = useState(null);

  const orderId = order?.orderId;
  const accountId = order?.account ?? null;
  const isListBond = !!(order?.tradingSessionSubId || order?.mktxListId);
  const canAct = String(order?.entryBy ?? "") === String(localStorage.getItem("userId") ?? "");

  useEffect(() => {
    setItems([]);
    setLoadedOrderId(null);
    setSent(false);
    setConfirmOpen(false);
    if (!isListBond || !orderId) return;
    let active = true;
    const token = localStorage.getItem("token");
    if (!token) return;
    // Failed or empty lookups leave the visible button disabled.
    fetchListRfqItems(token, orderId).then((result) => {
      if (active) {
        setItems(result || []);
        setLoadedOrderId(orderId);
      }
    }).catch(() => {
      if (active) setLoadedOrderId(orderId);
    });
    return () => {
      active = false;
    };
  }, [isListBond, orderId]);

  const handleConfirm = async () => {
    // Recheck if list eligibility, ownership or saving changed while confirmation was open.
    if (cancelDisabled) {
      setConfirmOpen(false);
      return;
    }
    const token = localStorage.getItem("token");
    if (!token) {
      showError("Authentication required");
      return;
    }
    if (!orderId || !accountId) {
      showError("Missing order context");
      return;
    }
    try {
      setLoading(true);
      const response = await cancelListOrder(token, orderId, accountId);
      if (isSuccess(response)) {
        showSuccess(
          response?.message || response?.data?.message || "List RFQ cancel request sent successfully"
        );
        setSent(true);
      } else {
        showError(response?.message || response?.data?.message || "Failed to cancel list RFQ");
      }
    } catch (error) {
      showError(error?.message || "Failed to cancel list RFQ");
    } finally {
      setLoading(false);
      setConfirmOpen(false);
    }
  };

  const protocolName = String(order?.protocol || "").toLowerCase();
  const supportedProtocol = ENHANCED_RFQ_PROTOCOLS.some((protocol) => protocol?.toLowerCase() === protocolName)
    || protocolName.includes("marketaxess");
  const cancelDisabled = disabled || loading || sent || !isListBond || !orderId || !supportedProtocol
    || !canAct || !accountId || loadedOrderId !== orderId || !hasCancellableListItem(items);

  return (
    <>
        <Button
          variant="outlined"
          color="error"
          onClick={() => setConfirmOpen(true)}
          disabled={cancelDisabled}
          // Wider than the other footer buttons' fixed 150px so the longer label fits on one line.
          sx={{
            textTransform: "none",
            fontWeight: "700",
            ...buttonSx,
            width: "auto",
            minWidth: "150px",
            whiteSpace: "nowrap",
          }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : "Cancel List RFQ"}
        </Button>
      <RfqFinalConfirmPopup
        open={confirmOpen}
        message={`Are you sure you want to cancel ${order?.listName || formatListId(order?.listId)}?`}
        loading={loading}
        onNo={() => setConfirmOpen(false)}
        onYes={handleConfirm}
      />
      <SnackbarComponent />
    </>
  );
};

export default ListCancelButton;
